import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { openNotification } from '@/api/notifications.api';
import PageContainer from '@/components/Container/PageContainer';
import { Button } from '@/components/UI/Button';
import InnerPageTitle from '@/components/UI/InnerPageTitle';
import { H3 } from '@/components/UI/Typography/Heading';
import { Flex } from '@/layout';
import { changeNotificationStatus } from '@/redux/notification/notification.slice';
import { useAppDispatch } from '@/redux/store.hook';
import type { NotificationCenterItem } from '@/types/notifications';
import { convertTiming } from '@/utils/convert';
import { errorHandler } from '@/utils/toastHandler';
import { resolveSafeNotificationLink } from '@/utils/notificationMarkdown';
import { CardWrapper, ContentWrapper } from './style';

const NotificationDetail = () => {
  // @ts-ignore -- repository translation inference exceeds TypeScript's instantiation depth.
  const { t } = useTranslation();
  const { notificationId = '' } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [item, setItem] = useState<NotificationCenterItem>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = Number(notificationId);
    if (!Number.isSafeInteger(id) || id <= 0) {
      navigate('/home/notifications', { replace: true });
      return;
    }
    let active = true;
    openNotification(id).then((response) => {
      if (!active) return;
      setItem(response.data.data);
      if (response.data.data.readAt) {
        dispatch(changeNotificationStatus({ id, readAt: response.data.data.readAt }));
      }
    }).catch(() => {
      errorHandler(t('error.server'));
      navigate('/home/notifications', { replace: true });
    }).finally(() => active && setLoading(false));

    return () => { active = false; };
  }, [dispatch, navigate, notificationId, t]);

  return <PageContainer title={<InnerPageTitle link="/home/notifications" subtitle={t('notifications')} />}>
    <CardWrapper>
      {loading || !item ? <ContentWrapper><span>{t('loading')}</span></ContentWrapper> : <>
        <H3 fontWeight="bold" marginBottom="8px">{item.title}</H3>
        <p>{convertTiming(item.occurredAt)}</p>
        <ReactMarkdown
          skipHtml
          allowedElements={['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'a']}
          components={{
            a: ({ node: _node, href, children, ...props }) => {
              const link = resolveSafeNotificationLink(href);
              if (!link.allowed) return <span>{children}</span>;

              return <a {...props} href={href} target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener noreferrer' : undefined}>{children}</a>;
            },
          }}
        >{item.bodyMarkdown || item.summary || ''}</ReactMarkdown>
        <Flex gridGap={15} marginTop={30}>
          {item.action.state === 'available' && item.action.target && <Button type="button" size="lg" variant="primary"
            rounded={false} onClick={() => navigate(item.action.target!)}>
            {item.action.label || t('buttons.showDetails')}
          </Button>}
          <Button type="button" size="lg" variant="primary-invert" rounded={false}
            onClick={() => navigate('/home/notifications')}>{t('buttons.back')}</Button>
        </Flex>
      </>}
    </CardWrapper>
  </PageContainer>;
};

export default NotificationDetail;
